import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyEliteMindUsers } from './companyEliteMindApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable athletes table for the Elite Mind section.
 *
 * "Shared" is shared reports — reports the athlete chose to put in front of the
 * club. A row can never reveal that somebody logged something and kept it to
 * themselves, because the endpoint behind it does not count those at all.
 *
 * Journal entries are a count and nothing more: no symptom, no sleep quality,
 * no training compliance. Every user of the population is listed, including
 * those with no sport profile; they show dashes rather than zeros.
 */
const EliteMindUsersTable = ({
  fetcher = fetchCompanyEliteMindUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const active = row.journalEntries > 0 || row.sharedReports > 0;
          return {
            ...row,
            sportText: row.sportName || '-',
            entriesText: active ? row.journalEntries.toLocaleString() : '-',
            sharedText: active ? row.sharedReports.toLocaleString() : '-',
            concussionText: row.concussions > 0 ? row.concussions.toLocaleString() : '-',
            lastEntryDate: row.lastEntryDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('EliteMindUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Sport', key: 'sportText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Journal', key: 'entriesText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Shared', key: 'sharedText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Concussion',
      key: 'concussionText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    {
      title: 'Last Entry',
      key: 'lastEntryDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search athletes"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

EliteMindUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default EliteMindUsersTable;
